import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import posthog from "posthog-js";
import { useAuth } from "../hooks/use-auth";
import type { AuthState, BetterAuthClient } from "../hooks/use-auth";

interface AuthContextType extends AuthState {
  isAuthenticated: boolean;
  isGuest: boolean;
  signIn: any;
  signUp: any;
  signOut: () => Promise<void>;
  refetch: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
  authClient: BetterAuthClient;
  onAuthUpdate?: (user: any, signOut: () => void) => void;
}

/**
 * Generic authentication provider component that provides authentication state
 * to all child components through React context
 * Better-auth automatically handles session management, restoration, and security
 */
export function AuthProvider({ children, authClient, onAuthUpdate }: AuthProviderProps) {
  const authState = useAuth(authClient);
  const { refetch } = authClient.useSession();

  // Wrap Proxy objects in stable functions to prevent React DevTools from serializing them
  // This prevents property access (valueOf, toString, etc.) that triggers HTTP requests
  const wrappedSignOut = useCallback(async () => {
    await authClient.signOut();
  }, [authClient]);

  // Create a wrapper that prevents React DevTools from accessing serialization methods
  // on better-auth Proxy objects by intercepting valueOf/toString calls
  const createSafeProxy = useCallback(<T extends object>(target: T): T => {
    return new Proxy(target, {
      get(_target, prop) {
        // Block access to methods that trigger serialization and HTTP requests
        if (prop === "valueOf") {
          return () => "[Proxy: signIn]";
        }
        if (prop === "toString") {
          return () => "[Proxy: signIn]";
        }
        if (prop === Symbol.toPrimitive) {
          return () => "[Proxy: signIn]";
        }
        // Allow all other property access to pass through
        return (target as any)[prop];
      },
    }) as T;
  }, []);

  // Capture signIn/signUp ONCE per client. Reading `authClient.signIn` returns a
  // brand-new Proxy on every property access (better-auth's dynamic path proxy),
  // so putting those reads in a deps array defeats memoization entirely — every
  // render published a new context value, re-rendering all consumers (OTO-158).
  const authActions = useMemo(
    () => ({
      signIn: createSafeProxy(authClient.signIn),
      signUp: createSafeProxy(authClient.signUp),
    }),
    [authClient, createSafeProxy],
  );

  const contextValue: AuthContextType = useMemo(
    () => ({
      ...authState,
      // Wrap Proxy objects to prevent React DevTools from accessing serialization methods
      signIn: authActions.signIn,
      signUp: authActions.signUp,
      signOut: wrappedSignOut,
      refetch,
    }),
    // authState is memoized in useAuth, so changes here reflect actual value changes
    [authState, authActions, wrappedSignOut, refetch],
  );

  // Update global auth context for shared routes
  useEffect(() => {
    onAuthUpdate?.(authState.user, wrappedSignOut);
  }, [authState.user, wrappedSignOut, onAuthUpdate]);

  // Tie PostHog (events, session replay, exceptions, feature flags) to the
  // authenticated user: identify on login, reset once on logout. Uses the
  // posthog-js singleton each app's PostHogProvider initialises; guarded so it
  // is a no-op when PostHog is unconfigured. The wasIdentified ref avoids
  // resetting (and churning the anonymous id) on the initial logged-out load.
  const authedUser = authState.user as
    | { id?: string; email?: string; name?: string; role?: string }
    | null
    | undefined;
  const wasIdentified = useRef(false);
  useEffect(() => {
    if (!(posthog as { __loaded?: boolean }).__loaded) return;
    if (authedUser?.id) {
      posthog.identify(authedUser.id, {
        email: authedUser.email,
        name: authedUser.name,
        role: authedUser.role,
      });
      wasIdentified.current = true;
    } else if (wasIdentified.current) {
      posthog.reset();
      wasIdentified.current = false;
    }
  }, [authedUser?.id, authedUser?.email, authedUser?.name, authedUser?.role]);

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access authentication context
 * Must be used within an AuthProvider
 */
export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }

  return context;
}

/**
 * Higher-order component for protecting routes that require authentication
 */
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: React.ComponentType,
) {
  return function AuthenticatedComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuthContext();

    // Show loading while auth state is loading (better-auth handles session restoration automatically)
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      if (fallback) {
        const FallbackComponent = fallback;
        return <FallbackComponent />;
      }

      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Authentication Required</h2>
            <p className="text-muted-foreground">Please sign in to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}

/**
 * Higher-order component for routes that should only be accessible to guests
 */
export function withGuest<P extends object>(
  Component: React.ComponentType<P>,
  redirectTo: string = "/dashboard",
) {
  return function GuestComponent(props: P) {
    const { isAuthenticated, isLoading } = useAuthContext();

    // Show loading while auth state is loading (better-auth handles session restoration automatically)
    if (isLoading) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (isAuthenticated) {
      // In a real app, you would use your router to redirect
      // For now, we'll just show a message
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground mb-4">Already Signed In</h2>
            <p className="text-muted-foreground">
              You are already signed in. Redirecting to {redirectTo}...
            </p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
}
